import React from "react";
import styles from "./ota-firmware-history.module.scss";
import plusIcon from "@src/assets/svg/plus-icon.svg";
import OTAModal from "../modals/ota-modal/ota-modal";
import { TabButtons } from "@src/components/atoms/tab-buttons/tab-buttons";
import {
  OTA_FIRMWARE_HISTORY as cssClassName,
  FIRMWARE_TABS,
  VERSIONS,
  BACKUPS,
  ActiveInstallationStatus,
  ProcessTypes,
} from "../../../cloud-connect/ota-update/constants";
import { ButtonWithIcon } from "@src/components/atoms/buttons/button-with-icon/button-with-icon";
import { OTAHistoryList } from "../ota-history-list/ota-history-list";
import {
  ActiveInstallationProcessPayload,
  ListItem,
  TabType,
} from "@src/types/ota-update";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import { useOTAFirmwareHistory } from "./use-ota-firmware-history";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface OTAFirmwareHistoryProps {
  activeOTAHistoryTab: TabType;
  setActiveOTAHistoryTab: React.Dispatch<React.SetStateAction<TabType>>;
  firmwares: ListItem[] | [];
  backups: ListItem[] | [];
  fetchMainList: () => Promise<void>;
  fetchBackups: () => Promise<void>;
  setIsFirmwareLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsBackupLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectPushFirmwareRow: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  onSelectPushFirmwareBackupRow: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  showingAnyBlock: boolean;
  activeInstallationData?: ActiveInstallationProcessPayload | null;
}

const OTAFirmwareHistory = ({
  activeOTAHistoryTab,
  setActiveOTAHistoryTab,
  firmwares,
  backups,
  fetchMainList,
  fetchBackups,
  setIsFirmwareLoading,
  setIsBackupLoading,
  onSelectPushFirmwareRow,
  onSelectPushFirmwareBackupRow,
  showingAnyBlock,
  activeInstallationData,
}: OTAFirmwareHistoryProps) => {
  const {
    texts,
    tabLabels,
    isModalOpen,
    setIsModalOpen,
    selectedRow,
    setSelectedRow,
    handleTabChange,
    closeFirmwareModal,
    showOTAFirmwareVersionDrawer,
    showOTAFirmwareBackupDrawer,
    getPermission,
  } = useOTAFirmwareHistory({
    setActiveOTAHistoryTab,
    setIsFirmwareLoading,
    setIsBackupLoading,
  });

  const isFirmwareUpdatePushInProgress =
    activeInstallationData?.type !== ProcessTypes.BACKUP &&
    (activeInstallationData?.status === ActiveInstallationStatus.APPROVED ||
      activeInstallationData?.status === ActiveInstallationStatus.IN_PROGRESS);
  const isCreateBackupDisabled = isFirmwareUpdatePushInProgress;
  const otaHistoryConfig: Record<
    TabType,
    {
      buttonText: string;
      onClick?: () => void;
      content: React.ReactNode;
    }
  > = {
    [FIRMWARE_TABS.VERSIONS]: {
      buttonText: texts.uploadFirmwareReleaseText,
      onClick: showOTAFirmwareVersionDrawer,
      content: (
        <OTAHistoryList
          key={VERSIONS}
          type={VERSIONS}
          setIsModalOpen={setIsModalOpen}
          setSelectedRow={setSelectedRow}
          list={firmwares}
          activeOTAHistoryTab={activeOTAHistoryTab}
          showingAnyBlock={showingAnyBlock}
        />
      ),
    },

    [FIRMWARE_TABS.BACKUPS]: {
      buttonText: texts.createBackupText,
      onClick: showOTAFirmwareBackupDrawer,
      content: (
        <OTAHistoryList
          key={BACKUPS}
          type={BACKUPS}
          setIsModalOpen={setIsModalOpen}
          setSelectedRow={setSelectedRow}
          list={backups}
          activeOTAHistoryTab={activeOTAHistoryTab}
          showingAnyBlock={showingAnyBlock}
        />
      ),
    },
  };

  return (
    <>
      <div className={styles[cssClassName]}>
        <div className={styles[`${cssClassName}__header`]}>
          <div>
            <TabButtons
              tabValue={tabLabels[activeOTAHistoryTab]}
              callback={handleTabChange}
              tabs={Object.values(tabLabels)}
            />
          </div>

          {activeOTAHistoryTab === FIRMWARE_TABS.VERSIONS && (
            <RenderIfUserHasPermission
              permission={getPermission(activeOTAHistoryTab)}
            >
              <ButtonWithIcon
                icon={plusIcon}
                fun={
                  otaHistoryConfig[activeOTAHistoryTab].onClick ??
                  (() => undefined)
                }
                text={otaHistoryConfig[activeOTAHistoryTab].buttonText}
                variant={["no-outline-with-green20-hover", "color-blue"]}
              />
            </RenderIfUserHasPermission>
          )}

          {activeOTAHistoryTab === FIRMWARE_TABS.BACKUPS && (
            <RenderIfUserHasPermission permission={"initiate-update-process"}>
              <ButtonWithIcon
                icon={plusIcon}
                fun={
                  otaHistoryConfig[activeOTAHistoryTab].onClick ??
                  (() => undefined)
                }
                text={otaHistoryConfig[activeOTAHistoryTab].buttonText}
                variant={["no-outline-with-green20-hover", "color-blue"]}
                disabled={isCreateBackupDisabled}
                tooltip={
                  isCreateBackupDisabled ? texts.createBackupDisabledText : ""
                }
              />
            </RenderIfUserHasPermission>
          )}
        </div>

        {otaHistoryConfig[activeOTAHistoryTab].content}
      </div>

      <OTAModal
        isOpen={isModalOpen}
        closeFirmwareModal={closeFirmwareModal}
        selectedRow={selectedRow}
        fetchMainList={fetchMainList}
        fetchBackups={fetchBackups}
        onSelectPushFirmwareRow={onSelectPushFirmwareRow}
        onSelectPushFirmwareBackupRow={onSelectPushFirmwareBackupRow}
        activeOTAHistoryTab={activeOTAHistoryTab}
        activeInstallationData={activeInstallationData}
      />
    </>
  );
};

export default OTAFirmwareHistory;
